import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";

import { createWabaTemplate, uploadHeaderSample } from "../../services/wabaTemplates.api";
import toastError from "../../errors/toastError";
import { parseTemplate, countVars } from "../WabaTemplateModal/helpers";
import { buildComponents, validateForm } from "./buildComponents";

const EMPTY = {
  whatsappId: "",
  name: "",
  category: "MARKETING",
  language: "es_PE",
  headerType: "NONE",
  headerText: "",
  headerExamples: [],
  headerHandle: "",
  headerMediaUrl: "",
  uploadingHeader: false,
  body: "",
  bodyExamples: [],
  footer: "",
  buttons: [],
};

// Lógica del modal de creación de plantilla HSM (0368 C6/C7): estado del formulario,
// botones dinámicos, preview en vivo (reutiliza parseTemplate) y submit a POST /waba-templates.
export function useWabaTemplateCreate({ open, wabaConnections, onCreated, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const only = wabaConnections?.length === 1 ? wabaConnections[0].id : "";
    setForm({ ...EMPTY, whatsappId: only });
  }, [open, wabaConnections]);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  // Cambiar el tipo de encabezado descarta el media subido previamente.
  const setHeaderType = (value) =>
    setForm((p) => ({ ...p, headerType: value, headerHandle: "", headerMediaUrl: "" }));

  const handleUploadHeader = async (file) => {
    if (!file) return;
    if (!form.whatsappId) {
      toast.info("Selecciona primero la conexión WABA.");
      return;
    }
    setForm((p) => ({ ...p, uploadingHeader: true }));
    try {
      const { data } = await uploadHeaderSample(form.whatsappId, file);
      setForm((p) => ({
        ...p,
        headerHandle: data.handle,
        headerMediaUrl: URL.createObjectURL(file),
        uploadingHeader: false,
      }));
      toast.success("Imagen subida a Meta.");
    } catch (err) {
      setForm((p) => ({ ...p, uploadingHeader: false }));
      toastError(err);
    }
  };

  const setExample = (which, i, value) =>
    setForm((p) => {
      const arr = [...p[which]];
      arr[i] = value;
      return { ...p, [which]: arr };
    });

  const headerVarCount = form.headerType === "TEXT" ? countVars(form.headerText) : 0;
  const bodyVarCount = countVars(form.body);

  const addButton = () =>
    setForm((p) => ({
      ...p,
      buttons: [...p.buttons, { type: "QUICK_REPLY", text: "", url: "", phone: "" }],
    }));

  const setButton = (i, field, value) =>
    setForm((p) => {
      const b = [...p.buttons];
      b[i] = { ...b[i], [field]: value };
      return { ...p, buttons: b };
    });

  const removeButton = (i) =>
    setForm((p) => ({ ...p, buttons: p.buttons.filter((_, idx) => idx !== i) }));

  // Preview en vivo: arma components desde el formulario y los parsea a la forma de UI.
  const preview = useMemo(() => {
    const parsed = parseTemplate(buildComponents(form));
    return {
      parsed,
      headerTextVars: form.headerExamples,
      bodyVars: form.bodyExamples,
      headerMediaUrl: form.headerMediaUrl,
    };
  }, [form]);

  const error = validateForm(form);

  const handleSubmit = async () => {
    if (error) {
      toast.info(error);
      return;
    }
    setSaving(true);
    try {
      const { data } = await createWabaTemplate({
        whatsappId: form.whatsappId,
        name: form.name,
        category: form.category,
        language: form.language,
        components: buildComponents(form),
      });
      toast.success(`Plantilla enviada a revisión de Meta (${data?.status || "PENDING"}).`);
      if (onCreated) onCreated(data);
      if (onClose) onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    set,
    setHeaderType,
    handleUploadHeader,
    setExample,
    headerVarCount,
    bodyVarCount,
    addButton,
    setButton,
    removeButton,
    preview,
    error,
    saving,
    handleSubmit,
  };
}
